import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import {
  Layers,
  TrendingUp,
  DollarSign,
  Eye,
  MousePointer,
  ArrowLeft,
  Calendar,
  RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#6366F1", "#10B981", "#F43F5E", "#F59E0B", "#8B5CF6", "#EC4899"];

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");
  const [performanceData, setPerformanceData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [breakdownData, setBreakdownData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [perfRes, revRes, breakRes] = await Promise.all([
        axios.get(`${API}/analytics/charts/campaign-performance?days=${days}`),
        axios.get(`${API}/analytics/charts/revenue-spend?days=${days}`),
        axios.get(`${API}/analytics/charts/campaign-breakdown`)
      ]);
      setPerformanceData(perfRes.data);
      setRevenueData(revRes.data);
      setBreakdownData(breakRes.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-night-paper border border-night-subtle rounded-lg p-3 shadow-lg">
          <p className="text-sm text-muted-foreground mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' && entry.name.includes('$') 
                ? `$${entry.value.toFixed(2)}` 
                : entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="analytics-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Back
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
                  <Layers className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading font-bold text-xl text-white">Analytics</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-[140px] bg-night-paper border-night-subtle" data-testid="days-select">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-night-paper border-night-subtle">
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="60">Last 60 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                onClick={fetchAnalytics}
                variant="outline"
                className="border-night-subtle"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { 
              label: "Total Impressions", 
              value: performanceData?.totals?.impressions?.toLocaleString() || "0",
              icon: Eye, 
              color: "text-primary" 
            },
            { 
              label: "Total Clicks", 
              value: performanceData?.totals?.clicks?.toLocaleString() || "0",
              icon: MousePointer, 
              color: "text-emerald" 
            },
            { 
              label: "Avg CTR", 
              value: `${performanceData?.totals?.avg_ctr || 0}%`,
              icon: TrendingUp, 
              color: "text-yellow-500" 
            },
            { 
              label: revenueData?.type === "publisher" ? "Total Revenue" : "Total Spend",
              value: `$${revenueData?.totals?.total_revenue?.toFixed(2) || revenueData?.totals?.total_spend?.toFixed(2) || "0.00"}`,
              icon: DollarSign, 
              color: "text-rose" 
            }
          ].map((stat, index) => (
            <motion.div
              key={index}
              className="p-6 rounded-lg bg-night-paper border border-night-subtle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-muted-foreground">{stat.label}</span>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <div className="font-heading text-2xl font-bold text-white">{stat.value}</div>
            </motion.div>
          ))}
        </div>

        {/* Performance Over Time Chart */}
        <motion.div 
          className="mb-8 p-6 rounded-lg bg-night-paper border border-night-subtle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="font-heading text-lg font-semibold text-white mb-6">
            Campaign Performance Over Time
          </h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={performanceData?.data || []}>
                <defs>
                  <linearGradient id="impressionsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6366F1" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="clicksGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis 
                  dataKey="date" 
                  stroke="#52525B" 
                  tick={{ fill: '#A1A1AA', fontSize: 12 }}
                  tickFormatter={(value) => value.slice(5)}
                />
                <YAxis stroke="#52525B" tick={{ fill: '#A1A1AA', fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="impressions" 
                  stroke="#6366F1" 
                  fillOpacity={1}
                  fill="url(#impressionsGradient)"
                  name="Impressions"
                />
                <Area 
                  type="monotone" 
                  dataKey="clicks" 
                  stroke="#10B981" 
                  fillOpacity={1}
                  fill="url(#clicksGradient)"
                  name="Clicks"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* CTR Trend Chart */}
          <motion.div 
            className="p-6 rounded-lg bg-night-paper border border-night-subtle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="font-heading text-lg font-semibold text-white mb-6">
              CTR Trend
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={performanceData?.data || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#52525B" 
                    tick={{ fill: '#A1A1AA', fontSize: 12 }}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis stroke="#52525B" tick={{ fill: '#A1A1AA', fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="ctr" 
                    stroke="#F59E0B" 
                    strokeWidth={2}
                    dot={{ fill: '#F59E0B', strokeWidth: 0, r: 3 }}
                    name="CTR %"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Revenue/Spend Chart */}
          <motion.div 
            className="p-6 rounded-lg bg-night-paper border border-night-subtle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="font-heading text-lg font-semibold text-white mb-6">
              {revenueData?.type === "publisher" ? "Revenue Trend" : "Spend Trend"}
            </h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData?.data || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#52525B" 
                    tick={{ fill: '#A1A1AA', fontSize: 12 }}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis stroke="#52525B" tick={{ fill: '#A1A1AA', fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey={revenueData?.type === "publisher" ? "revenue" : "spend"} 
                    fill="#F43F5E"
                    radius={[4, 4, 0, 0]}
                    name={revenueData?.type === "publisher" ? "Revenue $" : "Spend $"}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>

        {/* Campaign Breakdown */}
        {breakdownData && breakdownData.campaigns?.length > 0 && (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Campaign Performance Table */}
            <motion.div 
              className="p-6 rounded-lg bg-night-paper border border-night-subtle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <h2 className="font-heading text-lg font-semibold text-white mb-6">
                Campaign Performance Breakdown
              </h2>
              <div className="space-y-4">
                {breakdownData.campaigns.slice(0, 5).map((campaign, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg bg-night-bg border border-night-subtle"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <div>
                        <div className="font-semibold text-white text-sm">{campaign.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {campaign.impressions.toLocaleString()} impressions
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-white text-sm">
                        ${campaign.spend?.toFixed(2) || "0.00"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {campaign.clicks} clicks
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Campaign Distribution Pie Chart */}
            <motion.div 
              className="p-6 rounded-lg bg-night-paper border border-night-subtle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <h2 className="font-heading text-lg font-semibold text-white mb-6">
                Impression Distribution
              </h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={breakdownData.campaigns.slice(0, 5)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="impressions"
                      nameKey="name"
                    >
                      {breakdownData.campaigns.slice(0, 5).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                {breakdownData.campaigns.slice(0, 5).map((campaign, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs">
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-muted-foreground">{campaign.name}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AnalyticsPage;
